import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { initPixel, pageVue } from "@/lib/pixel";
import { initAnalytics, vuePage } from "@/lib/analytics";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n/I18nContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import Accueil from "@/pages/Accueil";
import Bienvenue from "@/pages/Bienvenue";
import Activer from "@/pages/Activer";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Estimations from "@/pages/Estimations";
import Mesures from "@/pages/Mesures";
import NewEstimate from "@/pages/NewEstimate";
import Settings from "@/pages/Settings";
import EstimateView from "@/pages/EstimateView";
import Prospects from "@/pages/Prospects";
import Admin from "@/pages/Admin";
import Tarifs from "@/pages/Tarifs";
import Confidentialite from "@/pages/Confidentialite";
import Conditions from "@/pages/Conditions";
import BanniereTemoins from "@/components/BanniereTemoins";
import { Toaster } from "@/components/ui/sonner";

/**
 * Pixel Meta : une seule initialisation, puis une vue de page à chaque
 * changement de route — sans quoi une application SPA ne déclare qu'une seule
 * page à Meta pour toute la visite.
 *
 * `initPixel` et `pageVue` ne font rien tant que le visiteur n'a pas accepté
 * (voir lib/consentement.js) : appeler ici sans condition reste donc sûr.
 */
function SuiviPixel() {
  const { pathname } = useLocation();
  useEffect(() => { initPixel(); initAnalytics(); }, []);
  useEffect(() => { pageVue(); vuePage(); }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      {/* La racine est la vitrine publique : l'application vit sous /dashboard. */}
      <Route path="/" element={<Accueil />} />
      <Route path="/login" element={<Login />} />
      <Route path="/tarifs" element={<Tarifs />} />
      <Route path="/pricing" element={<Tarifs />} />
      {/* Adresses écrites à la main dans la prospection (« roofborder.com/plans »).
          Sans ces alias, le catch-all renvoyait le couvreur à l'accueil : il
          arrivait ailleurs que sur la page qu'on lui avait promise. */}
      <Route path="/plans" element={<Tarifs />} />
      <Route path="/planes" element={<Tarifs />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/privacy" element={<Confidentialite />} />
      <Route path="/conditions" element={<Conditions />} />
      <Route path="/terms" element={<Conditions />} />
      {/* Retour de paiement Stripe, puis choix du mot de passe. */}
      <Route path="/bienvenue" element={<Bienvenue />} />
      <Route path="/welcome" element={<Bienvenue />} />
      <Route path="/activer" element={<Activer />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/new" element={<ProtectedRoute><NewEstimate /></ProtectedRoute>} />
      {/* Mesures seules : même atelier que /new, sans le chiffrage. */}
      <Route path="/mesures" element={<ProtectedRoute><Mesures /></ProtectedRoute>} />
      <Route path="/mesures/nouvelle" element={<ProtectedRoute><NewEstimate mode="mesure" /></ProtectedRoute>} />
      <Route path="/estimations" element={<ProtectedRoute><Estimations /></ProtectedRoute>} />
      <Route path="/settings/:onglet?" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/prospects" element={<ProtectedRoute><Prospects /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/estimate/:id" element={<ProtectedRoute><EstimateView /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Les notifications suivent le thème, sinon elles restent noires sur blanc. */
function Notifications() {
  const { theme } = useTheme();
  return <Toaster position="top-right" richColors theme={theme === "clair" ? "light" : "dark"} />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <I18nProvider>
            <AuthProvider>
              <SuiviPixel />
              <AppRoutes />
              <BanniereTemoins />
              <Notifications />
            </AuthProvider>
          </I18nProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
